import { inject, PLATFORM_ID, TransferState } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ResolveFn,
  RouterStateSnapshot,
} from '@angular/router';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { catchError, of, tap, throwError } from 'rxjs';
import { Response, Request } from 'express';

import { REQUEST, RESPONSE } from '@services';

import {
  PROVIDER_ERROR_KEY,
  PROVIDER_KEY,
  ProviderService,
} from './provider.service';
import {
  ProviderNotFoundError,
  ProviderPreviewExpiredError,
} from './error-handler.service';
import { ProviderInfo } from '@models';
import { HttpErrorResponse } from '@angular/common/http';

export const providerResolverFn: ResolveFn<ProviderInfo> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const providerService = inject(ProviderService);
  const transferState = inject(TransferState);
  const platformId = inject(PLATFORM_ID);
  const request: Request | null = inject(REQUEST, { optional: true });
  const response: Response | null = inject(RESPONSE, { optional: true });

  if (isPlatformBrowser(platformId)) {
    // Check for error marker from server
    if (transferState.hasKey(PROVIDER_ERROR_KEY)) {
      const errorType = transferState.get(PROVIDER_ERROR_KEY, '');
      transferState.remove(PROVIDER_ERROR_KEY);
      if (errorType === 'ProviderNotFoundError') {
        return throwError(() => new ProviderNotFoundError());
      }
      if (errorType === 'ProviderPreviewExpiredError') {
        return throwError(() => new ProviderPreviewExpiredError());
      }
    }

    if (transferState.hasKey(PROVIDER_KEY)) {
      const provider = transferState.get(PROVIDER_KEY, null);

      if (!provider) {
        return throwError(() => new ProviderNotFoundError());
      }

      transferState.remove(PROVIDER_KEY); // cleanup after use
      providerService.setProvider(provider);

      return of(provider);
    }

    // On browser, just return the current provider if available
    return providerService.getProvider(route.paramMap.get('slug') || '').pipe(
      tap(data => {
        transferState.set(PROVIDER_KEY, data);
      }),
      catchError(_error => {
        if (response) {
          response.statusCode = 404;
        }
        transferState.set(PROVIDER_ERROR_KEY, 'ProviderNotFoundError');
        return throwError(() => new ProviderNotFoundError());
      })
    );
  }

  // Extract slug from route params instead of manually parsing URL
  const slug = route.paramMap.get('slug');

  if (
    isPlatformServer(platformId) &&
    slug !== null &&
    request?.query &&
    !('preview' in request.query)
  ) {
    return providerService.getProvider(slug).pipe(
      tap(data => {
        transferState.set(PROVIDER_KEY, data);
      }),
      catchError(_error => {
        if (response) {
          response.statusCode = 404;
        }
        transferState.set(PROVIDER_ERROR_KEY, 'ProviderNotFoundError');
        return throwError(() => new ProviderNotFoundError());
      })
    );
  } else if (
    isPlatformServer(platformId) &&
    request?.query &&
    'preview' in request.query
  ) {
    return providerService
      .getProviderPreview(
        request.query['providerId'] as string,
        request.query['lang'] as string,
        request.query['token'] as string
      )
      .pipe(
        tap(data => {
          transferState.set(PROVIDER_KEY, data);
        }),
        catchError((error: HttpErrorResponse) => {
          if (response && (error.status === 401 || error.status === 403)) {
            response.statusCode = error.status;
            transferState.set(
              PROVIDER_ERROR_KEY,
              'ProviderPreviewExpiredError'
            );
            return throwError(() => new ProviderPreviewExpiredError());
          }

          // other not found errors
          if (response) {
            response.statusCode = error.status;
            transferState.set(PROVIDER_ERROR_KEY, 'ProviderNotFoundError');
            return throwError(() => new ProviderNotFoundError());
          }

          transferState.set(PROVIDER_ERROR_KEY, 'ProviderNotFoundError');
          return throwError(() => new ProviderNotFoundError());
        })
      );
  }

  if (response) {
    response.statusCode = 404;
  }

  transferState.set(PROVIDER_ERROR_KEY, 'ProviderNotFoundError');
  return throwError(() => new ProviderNotFoundError());
};
